/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */
import {
  Autocomplete,
  AutocompleteRenderOptionState,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  FormHelperText,
  Tooltip,
  TextField,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import BreadCrumbsComponent from "../../SharedComponents/BreadCrumbs/BreadCrumbsComponent";
import Title from "../../SharedComponents/Title/Title";
import { useEffect, useState, useRef, SetStateAction } from "react";

import "./CreateTenant.scss";
import IFile from "../../Image/types/File";
import useInput from "../../../hooks/user-input";
import { useNavigate } from "react-router-dom";
import Loader from "../../Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import addLogo from "../../../images/addLogo.png";
import DefaultProfileImgDark from "../../../images/DefaultProfileImgDark.png";
import {
  CreateorEditTenantDto,
  IErrorProps,
  MappingCountry,
  MappingRegion,
  MappingState,
  OptionsProduct,
  OptionsTimeZone,
} from "../../../models";
import OptionsUtilityType from "../../../models/OptionsModels/OptionsUtilityType";
import { UTILITY_TYPES_OPTIONS } from "../../../utils/constants";
import { formatLatitudeForUI, formatLongitudeForUI, latitudeToAPI, longitudeToAPI, validateLatitude, validateLongitude } from "../../../utils/coordinateUtils";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  Button,
  CameraIcon,
  Checkbox,
  Control,
  ControlError,
  Select,
  SelectOption,
  Input,
  Label,
  Stepper,
  Chip,
  ChipRemove,
} from "@forge/common";
import {
  createTenantDetails,
  getAvailableCurrencies,
  getAvailableDataCenters,
  getAvailableProducts,
  getAvailableTimeZones,
  getGeoMapping,
  uploadTenantLogoDetails,
} from "../../../store/Tenant/tenant-actions";
import updateErrorList from "../../../utils/methods/updateErrorList";
import DialogForError from "../../SharedComponents/DialogForError";
import ToastBarForError from "../../SharedComponents/ToastBarForError";
import GeoMapping from "../../../models/apiModels/GeoMapping";
import { NewEvent } from "../../../utils/methods/NewEvent";
import Footer from "../Footer/Footer";
import { StepperDTO } from "../../../models/componentModels/StepperDTO";
import ErrorHandlingComponent from "../../SharedComponents/ErrorHandlingComponent";
import { serverErrorClose } from "../../SharedComponents/serverErrorClose";
import { setErrorPopupsusingErrorCode } from "../../TenantUser/setErrorPopupsusingErrorCode";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import GetTokenValues from "../../../utils/methods/GetTokenValues";
import { normalizeOrganizationId } from "../../../utils/methods/normalizeOrganizationId";
import useLocalStorageChange from "../../../hooks/useLocalStorageChange";
import { useTranslation } from "@scuf/localization";
import { localeKeyMap } from '../../../localization/keys';

// Constants
const SELECT_ALL_PRODUCT = "Select All";

export interface StepperProps {
  stepperState: StepperDTO;
  setStepperState: (value: any) => void;
}

const CreateTenant: React.FC<StepperProps> = ({
  stepperState,
  setStepperState,
}) => {
  const [t] = useTranslation();
  const [theme, setCurrentTheme] = useState(
    localStorage.getItem("currentTheme") || "light"
  );
  const TenantNameRegex = /^[a-zA-Z0-9\s]*$/; // Only allows alphanumeric characters and spaces
  const AddressRegex = /^[a-zA-Z0-9,\s\.\-]*$/; // Only allows alphanumeric characters
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleIconClick = () => {
    fileInputRef.current?.click();
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const [selectedApplications, setSelectedApplications] = useState<
    OptionsProduct[]
  >([]);
  const [crumbs, setCrumbs] = useState([
    {
      id: "create-tenant",
      href: "/",
      label: t(localeKeyMap.home),
    },
  ]);
  const [enteredApp, setEnteredApp] = useState<Array<string>>([]);
  useEffect(() => {
    const selectedApp = selectedApplications?.map(
      (selectedApplication) => selectedApplication.productId
    );
    setEnteredApp(selectedApp);
  }, [selectedApplications]);

  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(false);
  const [allSelected, setAllSelected] = useState(false);

  const [enteredUtilityType, setUtilityType] = useState<OptionsUtilityType[]>([]);
  const [enteredUtilityTypeSubmit, setUtilityTypeSubmit] = useState<string[]>([]);
  const [allSelectedUT, setAllSelectedUT] = useState(false);
  const [showAllUT, setShowAllUT] = useState(false);
  const [isUtilityTypeTouched, setIsUtilityTypeTouched] = useState(false);

  const [enteredLatitude, setLatitude] = useState("");
  const [latitudehasError, setLatitudehasError] = useState<boolean>(false);
  const [enteredLongitude, setLongitude] = useState("");
  const [longitudehasError, setLongitudehasError] = useState<boolean>(false);

  const [, setAddressHasError] = useState<{ street?: boolean; city?: boolean; zipcode?: boolean }>({});
  const [enteredStreet, setStreet] = useState("");
  const [enteredCity, setCity] = useState("");

  useLocalStorageChange("currentTheme", (updatedTheme: any) => {
    if (updatedTheme) {
      setCurrentTheme(updatedTheme);
    }
  });

  // istanbul ignore next
  //for handelling select all feature
  const handleApplicationSelection = (
    event: any,
    selectedOptions: OptionsProduct[]
  ) => {
    // Ensure selectedOptions, optionsProduct is not null or undefined before proceeding
    if (!selectedOptions || !optionsProduct) return;

    const isSelectAllOptionSelected = selectedOptions.find(
      (option) => option.productName === SELECT_ALL_PRODUCT
    );

    if (isSelectAllOptionSelected) {
      if (selectedOptions?.length === optionsProduct?.length) {
        setSelectedApplications([]);
        setAllSelected(false);
        setIsTouched(true);
      } else {
        setSelectedApplications(optionsProduct.slice(1));
        setAllSelected(true);
        setIsTouched(true);
        setOpen(false);
      }
    } else {
      // option selected is not "Select All"
      if (selectedOptions?.length === optionsProduct?.length - 1) {
        setAllSelected(true);
        setOpen(false);
      } else {
        setAllSelected(false);
      }
      setSelectedApplications(selectedOptions);
      setIsTouched(true);
    }

    setIsTouched(true);
  };
  const [isTouched, setIsTouched] = useState(false);
  const [optionsProduct, setOptionsProduct] = useState<Array<OptionsProduct>>(
    []
  );

  const {
    value: enteredTenantName,
    isValid: enteredTenantNameIsValid,
    hasError: enteredTenantHasError,
    onSubmitError: enteredTenantOnSubmitError,
    valueChangeHandler: tenantNameChangeHandler,
    inputBlurHandler: tenantNameBlurHandler,
    onSubmitHandler: tenantSubmitHandler,
    regexError: regexhasError,
  } = useInput((value: any) => value.trim() !== "");
  const [enteredProduct, setProduct] = useState<string[]>([]);

  const {
    value: enteredheadquarterAddress,
    isValid: enteredheadquarterAddressIsValid,
    hasError: enteredheadquarterAddressHasError,
    onSubmitError: enteredheadquarterAddressOnSubmitError,
    valueChangeHandler: enteredheadquarterAddressChangeHandler,
    inputBlurHandler: enteredheadquarterAddressBlurHandler,
    onSubmitHandler: enteredheadquarterAddressSubmitHandler,
    regexError: regexhasErrorHeadquater,
  } = useInput((value: any) => value.trim() !== "");

  const {
    value: enteredCurrency,
    isValid: enteredCurrencyIsValid,
    hasError: enteredCurrencyHasError,
    onSubmitError: enteredCurrencySubmitError,
    valueChangeHandler: enteredCurrencyChangeHandler,
    inputBlurHandler: enteredCurrencyBlurHandler,
    onSubmitHandler: enteredCurrencySubmitHandler,
  } = useInput((value: any) => value !== "");

  const {
    value: enteredTimeZone,
    isValid: enteredTimeZoneIsValid,
    hasError: enteredTimeZoneHasError,
    onSubmitError: enteredTimeZoneSubmitError,
    valueChangeHandler: enteredTimeZoneChangeHandler,
    inputBlurHandler: enteredTimeZoneBlurHandler,
    onSubmitHandler: enteredTimeZoneSubmitHandler,
    reset: resetEnteredTimeZone,
  } = useInput((value: any) => value !== "");

  const {
    value: enteredDatacenter,
    isValid: enteredDatacenterIsValid,
    hasError: enteredDatacenterHasError,
    onSubmitError: enteredDatacenterSubmitError,
    valueChangeHandler: enteredDatacenterChangeHandler,
    inputBlurHandler: enteredDatacenterBlurHandler,
    onSubmitHandler: enteredDatacenterSubmitHandler,
  } = useInput((value: any) => value !== "");

  const {
    value: enteredState,
    isValid: enteredStateIsValid,
    hasError: enteredStateHasError,
    onSubmitError: enteredStateSubmitError,
    valueChangeHandler: enteredStateChangeHandler,
    inputBlurHandler: enteredStateBlurHandler,
    onSubmitHandler: enteredStateSubmitHandler,
    reset: resetEnteredState,
  } = useInput((value: any) => value !== "");

  const {
    value: enteredCountry,
    isValid: enteredCountryIsValid,
    hasError: enteredCountryHasError,
    onSubmitError: enteredCountrySubmitError,
    valueChangeHandler: enteredCountryChangeHandler,
    inputBlurHandler: enteredCountryBlurHandler,
    onSubmitHandler: enteredCountrySubmitHandler,
    reset: resetEnteredCountry,
  } = useInput((value: any) => value !== "");

  const [isCountryDisabled, setIsCountryDisabled] = useState(true);
  const [isStateDisabled, setIsStateDisabled] = useState(true);
  const [isTimeZoneDisabled, setIsTimeZoneDisabled] = useState(true);
  const [enteredOrganizationName, setEnteredOrganizationName] = useState("");
  const [enteredOrganizationId, setEnteredOrganizationId] = useState("");
  const [isOrganizationIdDirty, setIsOrganizationIdDirty] = useState(false);

  const tokenValues = GetTokenValues();
  let accessTokenPayload =
    tokenValues !== undefined
      ? tokenValues.accessTokenPayload
      : {
          sub: "",
          ext: {
            customer: "",
            tenant_id: "",
          },
        };

  const tokenInfo = {
    customer: accessTokenPayload?.ext?.customer,
    tenant: accessTokenPayload?.ext?.tenant_id,
    userId: accessTokenPayload?.sub,
  };
  const tenantId = tokenInfo.tenant;
  const {
    value: enteredRegion,
    isValid: enteredRegionIsValid,
    hasError: enteredRegionHasError,
    onSubmitError: enteredRegionSubmitError,
    valueChangeHandler: enteredRegionChangeHandler,
    inputBlurHandler: enteredRegionBlurHandler,
    onSubmitHandler: enteredRegionSubmitHandler,
  } = useInput((value: any) => value !== "");

  const {
    value: enteredZipcode,
    valueChangeHandler: enteredZipcodeChangeHandler,
    inputBlurHandler: enteredZipcodeBlurHandler,
    onSubmitHandler: enteredZipcodeSubmitHandler,
    regexError: regexhasErrorZipCode,
  } = useInput((value: any) => value !== "");

  const optionsDataCenter = useSelector(
    (state: RootState) => state.cart.availableDataCentres
  );
  // istanbul ignore next

  const optionstimeZone = useSelector(
    (state: RootState) => state.cart.availableTimeZones
  );
  const optionsCurrency = useSelector(
    (state: RootState) => state.cart.availableCurrencies
  );
  const optionsGeoMapping: GeoMapping = useSelector(
    (state: RootState) => state.cart.geoMapping
  );

  const optionsProductList = useSelector(
    (state: RootState) => state.cart.availableProducts
  );

  ///////////////Error Handling////////////////////////
  let utOptions: OptionsUtilityType[] = [
    { id: "Select All", name: "Select All" },
  ];
  const optionsUtilityType = utOptions.concat(UTILITY_TYPES_OPTIONS);
  const optionsUtilityTypeFromStore = UTILITY_TYPES_OPTIONS;

  ///////////////Error Handling////////////////////////
  const [isLoading, setIsLoading] = useState(false);
  const toastErrors = useRef<IErrorProps[]>([]);
  const [errorListGlobal, setErrorListGlobal] = useState<Array<IErrorProps>>(
    []
  );
  const [openDialogNoURLError, setOpenDialogNoURLError] = useState(false);
  const [openDialogServerError, setOpenDialogServerError] = useState(false);
  const [openToastError, setOpenToastError] = useState(false);

  const onDialogForServerCloseHandler = () => {
    serverErrorClose({
      errorListGlobal,
      setOpenDialogServerError,
      setErrorListGlobal,
    });
  };
  const onDialogForNOURLCloseHandler = () => {
    setOpenDialogNoURLError(false);
  };
  const handleToastBarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenToastError(false);
  };

  // istanbul ignore next
  useEffect(() => {
    setErrorPopupsusingErrorCode({
      errorListGlobal,
      setOpenDialogNoURLError,
      setOpenToastError,
      setOpenDialogServerError,
      openDialogNoURLError,
      openToastError,
      openDialogServerError,
    });
  }, [errorListGlobal]);

  //////////////Error Handling////////////////////////

  // Get Datacentres if not present
  useEffect(() => {
    if (optionsCurrency?.length === 0) {
      setIsLoading(true);
      dispatch(getAvailableDataCenters())
        .unwrap()
        .then(() => {})
        .catch((error: any) => {
          setErrorListGlobal((prevState) =>
            updateErrorList(error, [...prevState])
          );
        })
        .finally(() => setIsLoading(false));
    }
  }, []);
  // Get Geo mapping
  useEffect(() => {
    if (optionsGeoMapping?.countryDataMap?.length === 0) {
      setIsLoading(true);
      dispatch(getGeoMapping())
        .unwrap()
        .then(() => {})
        .catch((error: any) => {
          setErrorListGlobal((prevState) =>
            updateErrorList(error, [...prevState])
          );
        })
        .finally(() => setIsLoading(false));
    }
  }, []);

  // Get Timezones if not present
  useEffect(() => {
    if (optionstimeZone?.length === 0) {
      setIsLoading(true);
      dispatch(getAvailableTimeZones())
        .unwrap()
        .then(() => {})
        .catch((error: any) => {
          setErrorListGlobal((prevState) =>
            updateErrorList(error, [...prevState])
          );
        })
        .finally(() => setIsLoading(false));
    }
  }, []);
  // Get currencies if not present
  useEffect(() => {
    if (optionsCurrency?.length === 0) {
      setIsLoading(true);
      dispatch(getAvailableCurrencies())
        .unwrap()
        .then(() => {})
        .catch((error: any) => {
          setErrorListGlobal((prevState) =>
            updateErrorList(error, [...prevState])
          );
        })
        .finally(() => setIsLoading(false));
    }
  }, []);
  // Get available products if not present
  useEffect(() => {
    if (optionsProductList?.length === 0) {
      setIsLoading(true);
      dispatch(getAvailableProducts())
        .unwrap()
        .then(() => {})
        .catch((error: any) => {
          setErrorListGlobal((prevState) =>
            updateErrorList(error, [...prevState])
          );
        })
        .finally(() => setIsLoading(false));
    }
  }, []);

  ///Get API calls done
  useEffect(() => {
    let prodOptions: OptionsProduct[] = [
      { productId: SELECT_ALL_PRODUCT, settings: [], productName: SELECT_ALL_PRODUCT },
    ];

    prodOptions = prodOptions.concat(optionsProductList || []);
    setOptionsProduct(prodOptions);
  }, [optionsProductList]);
  useEffect(() => {
    const selectedApp = selectedApplications?.map(
      (selectedApps) => selectedApps.productId
    );
    setEnteredApp(selectedApp);
  }, [selectedApplications]);
  useEffect(() => {
    setUtilityTypeSubmit(
      optionsUtilityType
        .filter((n) => enteredUtilityType.indexOf(n) !== -1)
        .map((x) => x.id)
    );
  }, [enteredUtilityType]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const [currentImage, setCurrentImage] = useState<File>();
  const [previewImage, setPreviewImage] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>("");
  const [imageInfos, setImageInfos] = useState<Array<IFile>>([]);
  const dispatch: AppDispatch = useDispatch();

  const checkIfAnyTextFilled = () => {
    if (
      enteredTenantName === "" &&
      enteredheadquarterAddress === "" &&
      // enteredApplication === "" &&
      enteredTimeZone === "" &&
      enteredDatacenter === "" &&
      enteredCurrency === "" &&
      enteredCountry === "" &&
      enteredRegion === ""
    ) {
      if (enteredState === "" && !isStateDisabled) {
        return true;
      } else if (isStateDisabled) {
        return true;
      } else {
      }

      return false;
    } else return false;
  };

  //validate the form fields
  // istanbul ignore next
  //calling fromg the footer component
  function validate() {
    if (enteredApp?.length == 0) {
      setIsTouched(true);
    }
    if (enteredUtilityTypeSubmit?.length == 0) {
      setIsUtilityTypeTouched(true);
    }
    if (
      !enteredheadquarterAddressIsValid ||
      !enteredTenantNameIsValid ||
      !enteredCurrencyIsValid ||
      !enteredTimeZoneIsValid ||
      !enteredDatacenterIsValid ||
      !enteredRegionIsValid ||
      regexhasError ||
      regexhasErrorHeadquater ||
      regexhasErrorZipCode ||
      enteredApp?.length == 0
    ) {
      tenantSubmitHandler();
      enteredheadquarterAddressSubmitHandler();
      enteredCurrencySubmitHandler();
      enteredTimeZoneSubmitHandler();
      enteredDatacenterSubmitHandler();
      enteredRegionSubmitHandler();
      enteredZipcodeSubmitHandler();

      return;
    } else {
      setCreateOpen(true);
    }
  }

  const [regionMapping, setRegionMapping] = useState<Array<MappingRegion>>([]);
  const [countryMapping, setCountryMapping] = useState<Array<MappingCountry>>(
    []
  );
  const [stateMapping, setStateMapping] = useState<Array<MappingState>>([]);
  const [TimezoneMapping, setTimezoneMapping] = useState<
    Array<OptionsTimeZone>
  >([]);
  const [stateForDropDownCountryMapping, setStateForDropDownCountryMapping] =
    useState(1);
  const [stateForDropDownStateMapping, setStateForDropDownStateMapping] =
    useState(1);
  const [stateForDropDownTimeZoneMapping, setStateForDropDownTimeZoneMapping] =
    useState(1);

  useEffect(() => {
    const newregionMapping: Array<MappingRegion> = [];
    optionsGeoMapping?.regionDataMap?.map((x) => {
      newregionMapping.push(x.region);
    });
    setRegionMapping(newregionMapping);
  }, [optionsGeoMapping]);

  useEffect(() => {
    let indexOfRegionData = optionsGeoMapping?.regionDataMap.findIndex(
      (x) => x.region.id === +enteredRegion
    );
    let countries: Array<MappingCountry> = [];
    let timezone: Array<OptionsTimeZone> = [];

    if (indexOfRegionData !== -1) {
      countries = optionsGeoMapping?.regionDataMap[indexOfRegionData].countries;
      timezone = optionsGeoMapping?.regionDataMap[indexOfRegionData].timezones;
      setCountryMapping(countries);
      setTimezoneMapping(timezone);
    }
  }, [enteredRegion]);

  // All useState are for dropdown "key" field
  useEffect(() => {
    setStateForDropDownCountryMapping(stateForDropDownCountryMapping + 1);
  }, [countryMapping]);
  useEffect(() => {
    setStateForDropDownStateMapping(stateForDropDownStateMapping + 1);
  }, [enteredCountry]);
  useEffect(() => {
    setStateForDropDownTimeZoneMapping(stateForDropDownTimeZoneMapping + 1);
  }, [enteredRegion]);

  useEffect(() => {
    let x1 = optionsGeoMapping?.countryDataMap.findIndex(
      (x) => x.country.id === +enteredCountry
    );
    let state: Array<MappingState> = [];

    if (x1 !== -1) {
      state = optionsGeoMapping?.countryDataMap[x1].states;
      setStateMapping(state);
    }
  }, [enteredCountry]);
  /**Returns true if the enetered Forge customer id is invalid */
  const isForgeCustomerIdValid = (errMsg: string) =>
    errMsg.replace(/\s/g, "").toLowerCase().includes("forgecustomerid");
  // istanbul ignore next
  //for handelling submit , on clicking on popup , after validation is true
  async function onSubmitHandeller() {
    //event.preventDefault();
    if (enteredApp?.length == 0) {
      setIsTouched(true);
    }
    if (enteredUtilityTypeSubmit?.length == 0) {
      setIsUtilityTypeTouched(true);
    }

    if (
      !enteredheadquarterAddressIsValid ||
      !enteredTenantNameIsValid ||
      !enteredCurrencyIsValid ||
      !enteredTimeZoneIsValid ||
      !enteredDatacenterIsValid ||
      // !enteredForgeCustomerIdIsValid ||
      !enteredRegionIsValid ||
      enteredApp?.length == 0 ||
      enteredUtilityTypeSubmit?.length == 0 ||
      regexhasError ||
      // regexhasErrorTenantId ||
      regexhasErrorHeadquater ||
      regexhasErrorZipCode ||
      latitudehasError ||
      longitudehasError
    ) {
      tenantSubmitHandler();
      enteredheadquarterAddressSubmitHandler();
      enteredCurrencySubmitHandler();
      enteredTimeZoneSubmitHandler();
      enteredDatacenterSubmitHandler();
      //enteredForgeCustomerIdSubmitHandler();
      enteredRegionSubmitHandler();
      enteredZipcodeSubmitHandler();
      // enteredForgeTenantIdSubmitHandler();
      setCreateOpen(false);
      return;
    }
    setCreateOpen(false);

    const tenantToCreate: CreateorEditTenantDto = {
      tenantName: enteredTenantName.trim(),
      headquarterAddress: enteredheadquarterAddress,
      timezone: enteredTimeZone,
      datacenter: +(enteredDatacenter as unknown as string),
      currency: +(enteredCurrency as unknown as string),
      products: enteredApp,
      tenantId: tenantId,
      organizationName: enteredOrganizationName || "",
      organizationId: enteredOrganizationId || "",
      region: +(enteredRegion as unknown as string),
      address: {
        street: enteredStreet || "",
        city: enteredCity || "",
        zipcode: enteredZipcode || "",
        state:
          enteredCountry !== "1"
            ? ""
            : enteredState !== "" && !isStateDisabled
              ? +enteredState
              : "",
        country: enteredCountry !== "" ? +(enteredCountry as unknown as string) : "",
      },
      version: 0,
    };
    if (enteredUtilityTypeSubmit.length > 0) {
      tenantToCreate.utilityType = enteredUtilityTypeSubmit.join(",");
    }
    if (enteredLatitude) {
      const latitudeApiValue = latitudeToAPI(enteredLatitude);
      if (!latitudeApiValue) {
        setLatitudehasError(true);
        return;
      }
      tenantToCreate.latitude = latitudeApiValue;
    }
    if (enteredLongitude) {
      const longitudeApiValue = longitudeToAPI(enteredLongitude);
      if (!longitudeApiValue) {
        setLongitudehasError(true);
        return;
      }
      tenantToCreate.longitude = longitudeApiValue;
    }

    if (previewImage !== "") {
      setIsLoading(true);
      dispatch(createTenantDetails(tenantToCreate))
        .unwrap()

        .then((response) => {
          const parameters1: any = {
            tenantId: response.tenantId,
            fileName: currentImage,
            progressBar: null,
          };
          setIsLoading(true);
          dispatch(uploadTenantLogoDetails(parameters1))
            .then(() => {
              setStepperState({
                ...stepperState,
                currentStep: "Settings",
                steps: stepperState.steps.map((step) => {
                  if (step.id === "CreateTenant") {
                    return { ...step, status: "valid" };
                  }
                  return step;
                }),
              });
            })
            .catch((error) => {
              setErrorListGlobal((prevState) =>
                updateErrorList(error, [...prevState])
              );
            });
        })
        .catch((error: any) => {
          setErrorListGlobal((prevState) =>
            updateErrorList(error, [...prevState])
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(true);
      dispatch(createTenantDetails(tenantToCreate))
        .unwrap()
        .then(() =>
          setStepperState({
            ...stepperState,
            currentStep: "Settings",
            steps: stepperState.steps.map((step) => {
              if (step.id === "CreateTenant") {
                return { ...step, status: "valid" };
              }
              return step;
            }),
          })
        )
        .catch((err: any) => {
          setErrorListGlobal((prevState) =>
            updateErrorList(err.error.response, [...prevState])
          );

          // for error in errorCreateCustomer
          // setStepperState({
          //   ...stepperState,
          //   steps: stepperState.steps.map((step) => {
          //     if (step.id === "CreateTenant") {
          //       return { ...step, status: "invalid" };
          //     }
          //     return step;
          //   }),
          // });
          //mocking because we are getting error in errorCreateCustomer
          // setStepperState({
          //   ...stepperState,
          //   currentStep: "Settings",
          //   steps: stepperState.steps.map((step) => {
          //     if (step.id === "CreateTenant") {
          //       return { ...step, status: "valid" };
          //     }
          //     return step;
          //   }),
          // });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }
  // istanbul ignore next
  //for ImageUpload submit , on clicking on edit image icon
  const ImageUpload: React.FC = () => {
    const selectImage = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files as FileList;
      const file = files?.[0];
      var allowedExtensions = /(\.jpg|\.jpeg|\.png|\.svg)$/i;
      const maxFileSize = 5 * 1024 * 1024; //5 MB
      if (file) {
        if (!allowedExtensions.exec(files?.[0].name)) {
          alert("Invalid Logo file format, please select a Image file");
          return false;
        }
        if (file.size > maxFileSize) {
          alert("Invalid Logo file size, please select a file less than 5 MB");
          setPreviewImage("");
          return;
        }

        const selectedFiles = event.target.files as FileList;
        setCurrentImage(selectedFiles?.[0]);
        setPreviewImage(URL.createObjectURL(selectedFiles?.[0]));
        setProgress(0);
      }
    };
    return (
      <div>
        <Button size="regular" variant="secondary" onClick={handleIconClick}>
          Add Logo
        </Button>

        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={selectImage}
          style={{ display: "none" }}
        />
        {currentImage && progress > 0 && (
          <div className="progress my-3">
            <div
              className="progress-bar progress-bar-info"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              style={{ width: progress + "%", color: "red" }}
            >
              {progress}%
              <LinearProgress variant="determinate" value={progress} />
            </div>
          </div>
        )}

        {message && (
          <div className="alert alert-secondary mt-3" role="alert">
            {message}
          </div>
        )}
        {imageInfos?.length > 0 && (
          <div className="card mt-3">
            <div className="card-header">List of Images</div>
            <ul className="list-group list-group-flush">
              {imageInfos.map((img, index) => (
                <li className="list-group-item" key={index}>
                  <p>
                    <a href={img.url}>{img.name}</a>
                  </p>
                  <img src={img.url} alt={img.name} height="80px" />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  /**To deselct the products/applications */
  // istanbul ignore next
  //for handelling on delete
  const onDelete = (productId: any) => () => {
    let newSelectedApplications = selectedApplications.filter(
      (application) => application.productId !== productId
    );

    setSelectedApplications(newSelectedApplications);
    setAllSelected(false);
  };

  // istanbul ignore next
  const StreetAddressChangeHandler = (event: any) => {
    setStreet(event.target.value);
    setAddressHasError((prev: any) => ({ ...prev, street: false }));
  };

  // istanbul ignore next
  const CityChangeHandler = (event: any) => {
    setCity(event.target.value);
    setAddressHasError((prev: any) => ({ ...prev, city: false }));
  };

  // istanbul ignore next
  const LatitudeChangeHandler = (event: any) => {
    setLatitude(event.target.value);
    setLatitudehasError(false);
  };

  // istanbul ignore next
  const LatitudeBlurHandler = (event: any) => {
    const inputValue = event.target.value.trim();

    if (!inputValue) {
      setLatitude("");
      setLatitudehasError(false);
      return;
    }

    const formattedValue = formatLatitudeForUI(inputValue);
    const isValid =
      formattedValue !== "" &&
      validateLatitude(formattedValue);

    if (!isValid) {
      setLatitudehasError(true);
      return;
    }

    setLatitude(formattedValue);
    setLatitudehasError(false);
  };

  // istanbul ignore next
  const LongitudeChangeHandler = (event: any) => {
    setLongitude(event.target.value);
    setLongitudehasError(false);
  };

  // istanbul ignore next
  const LongitudeBlurHandler = (event: any) => {
    const inputValue = event.target.value.trim();

    if (!inputValue) {
      setLongitude("");
      setLongitudehasError(false);
      return;
    }

    const formattedValue = formatLongitudeForUI(inputValue);
    const isValid =
      formattedValue !== "" &&
      validateLongitude(formattedValue);

    if (!isValid) {
      setLongitudehasError(true);
      return;
    }

    setLongitude(formattedValue);
    setLongitudehasError(false);
  };

  // istanbul ignore next
  const handleUtilityTypeSelection = (
    event: React.ChangeEvent<{}>,
    selectedOptions: OptionsUtilityType[]
  ) => {
    setIsUtilityTypeTouched(true);
    if (!selectedOptions || !optionsUtilityType) return;
    if (selectedOptions.find((option) => option.id === "Select All")) {
      if (selectedOptions.length === optionsUtilityType.length) {
        setUtilityType([]);
        setAllSelectedUT(false);
      } else {
        setUtilityType(optionsUtilityType.slice(1));
        setAllSelectedUT(true);
      }
    } else {
      if (selectedOptions.length === optionsUtilityType.length - 1) {
        setAllSelectedUT(true);
      } else {
        setAllSelectedUT(false);
      }
      setUtilityType(selectedOptions);
    }
  };

  // istanbul ignore next
  const onDeleteUtilityType = (id: any) => () => {
    setUtilityType(enteredUtilityType.filter((ut) => ut.id !== id));
    setAllSelectedUT(false);
  };

  const renderLimitedSelectedUtilityTypeTags = () => {
    if (!enteredUtilityType) return;
    const firstTwoTags = enteredUtilityType.slice(0, 2).map((v: OptionsUtilityType) => (
      <Chip key={v.id} className="theme-chip mb-1">
        {v.name}
        <ChipRemove onClick={onDeleteUtilityType(v.id)} className="theme-chip-remove">
          {v.name}
        </ChipRemove>
      </Chip>
    ));
    return firstTwoTags.concat(
      <Chip className="theme-chip theme-chip-limit-tag" key="limit" onClick={() => setShowAllUT(true)}>
        {`+${enteredUtilityType.length - 2}`}
      </Chip>
    );
  };

  const renderAllSelectedUtilityTypeTags = () => {
    return enteredUtilityType.map((v: OptionsUtilityType) => (
      <Chip key={v.id} className="theme-chip mb-1">
        {v.name}
        <ChipRemove onClick={onDeleteUtilityType(v.id)} className="theme-chip-remove">
          {v.name}
        </ChipRemove>
      </Chip>
    ));
  };

  let chipStyle = {
    margin: "0 5px 5px 0",
    borderRadius: "4px",
    border: "1px solid F0F0F0",
    backgroundColor: "#FFF",
    boxShadow: "1px 1px 1px 0px rgba(0, 0, 0, 0.15)",
    fontWeight: "400",
    color: "#606060",
  };

  /**To render selected applicaton/product chips/tags with limit tag*/
  // istanbul ignore next
  //for handelling select all feature
  const renderLimitedSelectedApplicationTags = () => {
    let firstTwoTags = [];
    firstTwoTags = selectedApplications.slice(0, 2).map((v: any) => (
      <Chip className="theme-chip" key={v.productId}>
        {v.productName}
        <ChipRemove
          onClick={onDelete(v.productId)}
          className="theme-chip-remove"
          children=""
        />
      </Chip>
    ));
    return firstTwoTags.concat(
      <Chip
        className="theme-chip"
        key="limit"
        onClick={() => {
          setShowAll(true);
        }}
      >
        {`+${selectedApplications.length - 2}`}
      </Chip>
    );
  };

  /**To render all selected applications/products tags/chips */
  const renderAllSelectedApplicationTags = () => {
    return selectedApplications.map((v: any) => (
      <Chip className="theme-chip" key={v.productId}>
        {v.productName}
        <ChipRemove
          onClick={onDelete(v.productId)}
          className="theme-chip-remove"
          children=""
        />
      </Chip>
    ));
  };

  const regionSelectOption = () => {
    return optionsGeoMapping?.regionDataMap
      ?.slice()
      .sort((a, b) => a?.region?.name?.localeCompare(b?.region?.name))
      .map((region: any) => (
        <SelectOption
          value={region?.region?.id}
          label={region?.region?.name}
          key={region?.region?.id}
        />
      ));
  };

  const countrySelectOption = () => {
    return countryMapping
      ?.slice()
      .sort((a, b) => a?.name?.localeCompare(b?.name))
      .map((country: any) => (
        <SelectOption
          value={country?.id}
          label={country?.name}
          key={country?.id}
        />
      ));
  };

  const stateSelectOption = () => {
    return stateMapping
      ?.slice()
      .sort((a, b) => a?.name?.localeCompare(b?.name))
      .map((state: any) => (
        <SelectOption value={state?.id} label={state?.name} key={state?.id} />
      ));
  };

  const currencySelectOption = () => {
    return optionsCurrency
      ?.slice()
      .sort((a, b) => a?.name?.localeCompare(b?.name))
      .map((currency: any) => (
        <SelectOption
          value={currency?.id}
          label={currency?.name}
          key={currency?.id}
        />
      ));
  };

  const dataCenterSelectOption = () => {
    return optionsDataCenter
      ?.slice()
      .sort((a, b) => a?.name?.localeCompare(b?.name))
      .map((dataCenter: any) => (
        <SelectOption
          value={dataCenter?.id}
          label={dataCenter?.name}
          key={dataCenter?.id}
        />
      ));
  };

  const timezoneSelectOption = () => {
    return TimezoneMapping
      ?.slice()
      .sort((a, b) => a?.name?.localeCompare(b?.name))
      .map((timezone: any) => (
        <SelectOption
          value={timezone?.id}
          label={timezone?.name}
          key={timezone?.id}
        />
      ));
  };

  return (
    <>
      {/* <div className="d-flex main_form_wrap"> */}
      <div className="form_logo_wrap text-center color-bg-img">
        <div className="form_logo_image color-bg-profile-img-wrapper">
          {previewImage === "" ? (
            <img
              src={theme === "light" ? addLogo : DefaultProfileImgDark}
              alt="Logo"
              className=""
            />
          ) : (
            <img src={previewImage} alt="Logo" className="" />
          )}
        </div>
        <ImageUpload />
      </div>
      <div className="form_box">
        <h4>{t(localeKeyMap.profileInformation)}</h4>
        <div className="form_row">
          <div className="form_col">
            <Label requiredText="*"> {t(localeKeyMap.tenantNameLabel)} * </Label>
            <Input
              required={true}
              id="TenantName"
              className="tenant-name"
              placeholder={t(localeKeyMap.tenantNamePlaceholder)}
              size="regular"
              value={enteredTenantName}
              onBlur={tenantNameBlurHandler}
              onChange={(event: any) => {
                tenantNameChangeHandler(event, TenantNameRegex);
              }}
              maxLength={120}
              {...((enteredTenantHasError ||
                enteredTenantOnSubmitError ||
                regexhasError) && {
                error: true,
              })}
            />
            {(enteredTenantHasError ||
              regexhasError ||
              enteredTenantOnSubmitError) &&
              (regexhasError ? (
                <ControlError>
                  {t(localeKeyMap.tenantNameError)}
                </ControlError>
              ) : (
                <ControlError>{t(localeKeyMap.requiredField)}</ControlError>
              ))}
          </div>

          <div className="form_col">
            <Label requiredText="*"> {t(localeKeyMap.headquartersAddress)} * </Label>
            <Input
              className="headquarters-address"
              id="Headquarters-Address"
              placeholder={t(localeKeyMap.headquartersAddress)}
              size="regular"
              value={enteredheadquarterAddress}
              maxLength={32}
              onBlur={enteredheadquarterAddressBlurHandler}
              onChange={(event: any) =>
                enteredheadquarterAddressChangeHandler(event, AddressRegex)
              }
              {...((enteredheadquarterAddressHasError ||
                enteredheadquarterAddressOnSubmitError ||
                regexhasErrorHeadquater) && {
                error: true,
              })}
            />

            {(enteredheadquarterAddressHasError ||
              regexhasErrorHeadquater ||
              enteredheadquarterAddressOnSubmitError) &&
              (regexhasErrorHeadquater ? (
                <ControlError>
                  Address can only contain alphabets, numbers, dots(.)
                  hyphens(-) and comma(,){" "}
                </ControlError>
              ) : (
                <ControlError>{t(localeKeyMap.requiredField)}</ControlError>
              ))}
          </div>
          <div className="form_col">
            <Label>{t(localeKeyMap.organizationName)} <span className="text-muted"> - Optional</span></Label>
            <Input
              className="organization-name"
              id="Organization-Name"
              placeholder={t(localeKeyMap.organizationName)}
              size="regular"
              value={enteredOrganizationName}
              maxLength={120}
              onChange={(event: any) => {
                const organizationName = event.target.value;
                setEnteredOrganizationName(organizationName);
                if (!isOrganizationIdDirty) {
                  setEnteredOrganizationId(
                    normalizeOrganizationId(organizationName)
                  );
                }
              }}
            />
          </div>
          <div className="form_col">
            <Label>{t(localeKeyMap.organizationId)} <span className="text-muted"> - Optional</span></Label>
            <Input
              className="organization-id"
              id="Organization-ID"
              placeholder={t(localeKeyMap.organizationId)}
              size="regular"
              value={enteredOrganizationId}
              maxLength={120}
              onChange={(event: any) => {
                setIsOrganizationIdDirty(true);
                setEnteredOrganizationId(event.target.value);
              }}
            />
          </div>
          <div className="form_col">
            <Label requiredText="*"> {t(localeKeyMap.region)} *</Label>
            <Select
              className="dropdown_col"
              id="Region"
              key={`region-select-${optionsGeoMapping?.regionDataMap?.length}`}
              placeholder={t(localeKeyMap.region)}
              size="regular"
              value={Number(enteredRegion)}
              data-testid="SelectRegion"
              //onBlur={enteredRegionBlurHandler}
              onChange={(event: any) => {
                // const newEvent = NewEvent(event);

                enteredRegionChangeHandler(event, /.*/);
                setIsCountryDisabled(false);
                setIsTimeZoneDisabled(false);
                resetEnteredCountry();
                resetEnteredTimeZone();
                resetEnteredState();
              }}
              // Enable country field after selecting "Region" field

              {...((enteredRegionHasError || enteredRegionSubmitError) && {
                state: "error",
              })}
            >
              {regionSelectOption()}
            </Select>
            {(enteredRegionHasError || enteredRegionSubmitError) && (
              <ControlError>{t(localeKeyMap.requiredField)}</ControlError>
            )}
          </div>
          <div className="form_col">
            <Label>{t(localeKeyMap.country)} <span className="text-muted"> - Optional</span></Label>
            <Tooltip
              title={isCountryDisabled ? t(localeKeyMap.selectRegionFirst) : ""}
            >
              <Select
                key={`country-select-${stateForDropDownCountryMapping}`}
                id="Country"
                className="dropdown_col"
                placeholder={t(localeKeyMap.country)}
                size="regular"
                disabled={isCountryDisabled}
                data-testid="SelectCountry"
                value={Number(enteredCountry)}
                onChange={(event: any) => {              
                  enteredCountryChangeHandler(event, /.*/);
                  resetEnteredState();
                  if (+event.target.value === 1) {
                    setIsStateDisabled(false);
                  } else {
                    setIsStateDisabled(true);
                  }
                }}
              >
                {countrySelectOption()}
              </Select>
            </Tooltip>
          </div>
          <div className="form_col">
            <Label>{t(localeKeyMap.state)} <span className="text-muted"> - Optional</span></Label>
            <Tooltip
              title={
                isStateDisabled && enteredCountry === "1"
                  ? t(localeKeyMap.selectCountryFirst)
                  : ""
              }
            >
              <Select
                id="State"
                className="dropdown_col"
                key={`state-select-${stateForDropDownStateMapping}`}
                value={Number(enteredState)}
                placeholder={t(localeKeyMap.state)}
                size="regular"
                disabled={isStateDisabled}
                data-testid="SelectState"
                onChange={(event: any) => {
                  enteredStateChangeHandler(event, /.*/);
                }}
              >
                {stateSelectOption()}
              </Select>
            </Tooltip>
          </div>

          <div className="form_col">
            <Label> {t(localeKeyMap.zipCode)} </Label>
            <Input
              className="zip-code"
              id="Zip-Code"
              placeholder={t(localeKeyMap.zipCode)}
              value={enteredZipcode}
              size="regular"
              onChange={(Event: any) => {
                enteredZipcodeChangeHandler(Event, /^[a-zA-Z0-9\s-]{3,15}$/);
              }}
            />

            {regexhasErrorZipCode && (
              <ControlError>
                Zipcode can only contain alphabets, numbers, hyphens(-) with
                3-15 characters.
              </ControlError>
            )}
          </div>
          <div className="form_col">
            <Label className="mb-3">{t(localeKeyMap.streetAddress)} <span className="text-muted"> - Optional</span></Label>
            <Input
              className="streetAddress"
              id="streetAddress"
              placeholder={t(localeKeyMap.streetAddress)}
              value={enteredStreet}
              size="regular"
              onChange={(Event: any) => { StreetAddressChangeHandler(Event); }}
            />
          </div>
          <div className="form_col">
            <Label className="mb-3">{t(localeKeyMap.city)} <span className="text-muted"> - Optional</span></Label>
            <Input
              className="city"
              id="city"
              placeholder={t(localeKeyMap.city)}
              value={enteredCity}
              size="regular"
              onChange={(Event: any) => { CityChangeHandler(Event); }}
            />
          </div>
          <div className="form_col">
            <Label className="mb-3">{t(localeKeyMap.latitude)} <span className="text-muted"> - Optional</span></Label>
            <Input
              className="latitude"
              id="latitude"
              placeholder={t(localeKeyMap.latitude)}
              value={enteredLatitude}
              size="regular"
              onChange={(Event: any) => { LatitudeChangeHandler(Event); }}
              onBlur={(Event: any) => { LatitudeBlurHandler(Event); }}
            />
            {latitudehasError && enteredLatitude !== "" && (
              <ControlError>{t(localeKeyMap.latitudeHelperText)}</ControlError>
            )}
          </div>
          <div className="form_col">
            <Label className="mb-3">{t(localeKeyMap.longitude)}<span className="text-muted"> - Optional</span></Label>
            <Input
              className="longitude"
              id="longitude"
              placeholder={t(localeKeyMap.longitude)}
              value={enteredLongitude}
              size="regular"
              onChange={(Event: any) => { LongitudeChangeHandler(Event); }}
              onBlur={(Event: any) => { LongitudeBlurHandler(Event); }}
            />
            {longitudehasError && enteredLongitude !== "" && (
              <ControlError>{t(localeKeyMap.longitudeHelperText)}</ControlError>
            )}
          </div>
        </div>
        <h4 className="other_info_title">{t(localeKeyMap.otherInformation)}</h4>
        <div className="form_row">
          <div className="form_col">
            <Label requiredText="*"> {t(localeKeyMap.tenantID)} * </Label>
            <Input
              className="tenant-id"
              id="Tenant-ID"
              disabled={true}
              placeholder={t(localeKeyMap.tenantID)}
              size="regular"
              value={tenantId}
              maxLength={32}
            />
          </div>
          <div className="form_col">
            <Label requiredText="*"> {t(localeKeyMap.tenantCurrencyLabel)} * </Label>
            <Select
              key={`currency-select-${optionsCurrency?.length}`}
              id="Tenant-Currency"
              placeholder={t(localeKeyMap.tenantCurrencyLabel)}
              size="regular"
              className="dropdown_col"
              data-testid="SelectCurrency"
              {...((enteredCurrencyHasError || enteredCurrencySubmitError) && {
                state: "error",
              })}
              // onChange={enteredCurrencyChangeHandler}
              onChange={(event: any) => {
                enteredCurrencyChangeHandler(event, /.*/);
              }}
              value={Number(enteredCurrency)}
            >
              {currencySelectOption()}
            </Select>
            {(enteredCurrencyHasError || enteredCurrencySubmitError) && (
              <ControlError>{t(localeKeyMap.requiredField)}</ControlError>
            )}
          </div>
          <div className="form_col">
            <Label requiredText="*"> {t(localeKeyMap.tenantDataCenterLabel)} * </Label>
            <Select
              className="dropdown_col"
              key={`datacenter-select-${optionsDataCenter?.length}`}
              id="Tenant-Data-Center"
              placeholder={t(localeKeyMap.tenantDataCenterLabel)}
              value={Number(enteredDatacenter)}
              data-testid="SelectDataCenter"
              size="regular"
              {...((enteredDatacenterHasError ||
                enteredDatacenterSubmitError) && {
                state: "error",
              })}
              onChange={(event: any) => {
                enteredDatacenterChangeHandler(event, /.*/);
              }}
            >
              {dataCenterSelectOption()}
            </Select>
            {(enteredDatacenterHasError || enteredDatacenterSubmitError) && (
              <ControlError>{t(localeKeyMap.requiredField)}</ControlError>
            )}
          </div>
          <div className="form_col">
            <Label requiredText="*"> {t(localeKeyMap.timeZone)} * </Label>
            <Tooltip
              title={isTimeZoneDisabled ? t(localeKeyMap.selectRegionFirst) : ""}
            >
              <Select
                className="dropdown_col"
                id="Tenant-TimeZone"
                key={`timezone-select-${stateForDropDownTimeZoneMapping}`}
                placeholder={t(localeKeyMap.tenantTimeZone)}
                data-testid="SelectTimeZone"
                value={enteredTimeZone}
                size="regular"
                // onBlur={enteredTimeZoneBlurHandler}
                onChange={(event: any) => {
                  enteredTimeZoneChangeHandler(event, /.*/);
                }}
                {...((enteredTimeZoneHasError ||
                  enteredTimeZoneSubmitError) && {
                  state: "error",
                })}
              >
                {timezoneSelectOption()}
              </Select>
            </Tooltip>
            {/* 19 Feb Undo this change */}
            {(enteredTimeZoneHasError || enteredTimeZoneSubmitError) && (
              <ControlError>{t(localeKeyMap.requiredField)}</ControlError>
            )}
          </div>
          <div className="form_col">
            <Label requiredText="*"> {t(localeKeyMap.selectProduct)} * </Label>
            <Autocomplete
              multiple
              options={[
                ...optionsProduct.filter(option => option.productName === SELECT_ALL_PRODUCT),
                ...optionsProduct
                  .filter(option => option.productName !== SELECT_ALL_PRODUCT)
                  .slice()
                  .sort((a, b) => a.productName.localeCompare(b.productName))
              ]}
              getOptionLabel={(option) => option.productName}
              className={`multiselect-dropdown ${
                enteredApp?.length === 0 && isTouched && "disabled"
              }`}
              value={selectedApplications}
              onChange={handleApplicationSelection}
              disableCloseOnSelect
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              data-testid="SelectProduct"
              renderTags={() => null}
              renderOption={(props, option, { selected }) => {
                // Check if option is not null or undefined before accessing its properties
                if (!option) return;

                const selectAllProps =
                  option?.productName === SELECT_ALL_PRODUCT
                    ? { checked: allSelected }
                    : {};
                return (
                  <li {...props}>
                    <Checkbox
                      id="productId"
                      // icon={icon}
                      // checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                      {...selectAllProps}
                    />
                    {option.productName}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  autoComplete={"off"}
                  className="mui-autocomplete-placeholder"
                  value={enteredApp}
                   data-testid="SelectProductTextField"
                  onBlur={() => {
                    setShowAll(false);
                    setIsTouched(true);
                  }}
                  {...(enteredApp?.length === 0 &&
                    isTouched && {
                      error: true,
                    })}
                  sx={{
                    "& .MuiFormHelperText-root": {
                      marginTop: 0.1,
                      lineHeight: 0.9,
                    },
                    "& .MuiAutocomplete-root .MuiInputBase-input": {
                      height: "2rem",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#000",
                      fontSize: "14px",
                      opacity: "1",
                    },
                  }}
                  placeholder={
                    selectedApplications?.length === 0 ? t(localeKeyMap.productPlaceholder) : ""
                  }
                  {...params}
                />
              )}
            />
            {enteredApp?.length === 0 && isTouched && (
              <ControlError>{t(localeKeyMap.requiredField)}</ControlError>
            )}
            <Box
              sx={{
                marginTop: "5px",
              }}
            >
              {!showAll
                ? selectedApplications?.length > 2
                  ? renderLimitedSelectedApplicationTags()
                  : renderAllSelectedApplicationTags()
                : renderAllSelectedApplicationTags()}
            </Box>
          </div>
          <div className="form_col">
            <Label className="mb-3" requiredText="*">
              {t(localeKeyMap.selectUtilityTypeLabel)} *
            </Label>
            <Autocomplete
              data-testid="SelectUtilityType"
              className={`multiselect-dropdown`}
              multiple
              id="checkboxes-utility-types"
              options={[
                ...optionsUtilityType.filter(option => option.name === "Select All"),
                ...optionsUtilityType
                  .filter(option => option.name !== "Select All")
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
              ]}
              value={enteredUtilityType}
              disableCloseOnSelect
              getOptionLabel={(option) => option.name}
              onChange={handleUtilityTypeSelection}
              renderTags={() => null}
              renderOption={(props, option: OptionsUtilityType, { selected }) => {
                if (!option) return null;
                const isSelectAllOption = option.name === "Select All";
                return (
                  !allSelectedUT && (
                    <li {...props}>
                      <Checkbox
                        id={`utility-type-${option.id}`}
                        style={{ marginRight: 8 }}
                        checked={isSelectAllOption ? allSelectedUT : selected}
                      />
                      {option.name}
                    </li>
                  )
                );
              }}
              renderInput={(params) => (
                <TextField
                  autoComplete={"off"}
                  placeholder={enteredUtilityType.length === 0 ? t(localeKeyMap.selectUtilityTypeLabel) : ""}
                  onBlur={() => {
                    setShowAllUT(false);
                    setIsUtilityTypeTouched(true);
                  }}
                  sx={{
                    "& .MuiFormHelperText-root": { marginTop: 0.1, lineHeight: 0.9 },
                    "& .MuiAutocomplete-root .MuiInputBase-input": { height: "2rem" },
                    "& .MuiInputBase-input::placeholder": { fontSize: "14px", fontWeight: "700", opacity: "1" },
                  }}
                  {...(enteredUtilityTypeSubmit.length === 0 &&
                    isUtilityTypeTouched && {
                      error: true,
                    })}
                  {...params}
                />
              )}
            />
            {enteredUtilityTypeSubmit.length === 0 && isUtilityTypeTouched && (
              <ControlError>{t(localeKeyMap.requiredField)}</ControlError>
            )}
            <Box sx={{ marginTop: "5px" }}>
              {!showAllUT
                ? enteredUtilityType.length > 3
                  ? renderLimitedSelectedUtilityTypeTags()
                  : renderAllSelectedUtilityTypeTags()
                : renderAllSelectedUtilityTypeTags()}
            </Box>
          </div>
        </div>
      </div>
      {/* </div> */}
      <Footer
        cancelButtonRequired={false}
        openDialogCreate={createOpen}
        setOpenDialogCreate={setCreateOpen}
        openDialogCreateHeading={t(localeKeyMap.setupTenant)}
        openDialogCreateContent={t(localeKeyMap.saveTenantConfirmation)}
        openDialogonClose={false}
        openDialogCancel={false}
        dialogCreatePrimaryBtnLabel={t(localeKeyMap.confirm)}
        dialogCreateSecondaryBtnLabel={t(localeKeyMap.cancel)}
        setOpenDialogCancel={function (value: boolean): void {}}
        createBtnLabel={t(localeKeyMap.next)}
        cancelBtnLabel={t(localeKeyMap.cancel)}
        checkIfAnyTextFilled={checkIfAnyTextFilled}
        onSubmit={onSubmitHandeller}
        validate={validate}
      />
      <ErrorHandlingComponent
        openToast={openToastError}
        onCloseToast={handleToastBarClose}
        errorList={errorListGlobal.concat(toastErrors.current)}
        openServerError={openDialogServerError}
        onCloseServerError={onDialogForServerCloseHandler}
        openNoURL={openDialogNoURLError}
        onCloseNoURL={onDialogForNOURLCloseHandler}
        openDialogforConflict={false}
        onCloseServerConflictError={function (): void {}}
      />
      {isLoading && <Loader />}
    </>
  );
};

export default CreateTenant;